import type { TPlayerRegistrationResponse } from '@lucimark/shared/database/players-directory-schema'
import type { IWebOtaUpdateEventPayload } from '@lucimark/shared/models/player-update'
import type { IAuthenticationService } from '@/services/AuthenticationService'
import { resetPlayerAuthAmbiguousFailureStreak } from '@/helpers/playerRegistrationAuthPolicy'

export type TAmbiguousDirectoryGateResult
  = | 'aborted_refreshed'
    | 'proceed_clear'
    | 'aborted_inconclusive'
  /** Directory row is consumed (pairing) but tenant registration-status still succeeds. */
    | 'aborted_still_registered'

export type TRegistrationClearDirectoryGateMode = 'revoke' | 'ambiguous'

type TFetchRegistration = (opts?: {
  hardwareCapacity?: import('@lucimark/shared/models/playback-heartbeat').IHardwareCapacitySnapshot
  omitTenantCredentials?: boolean
}) => Promise<TPlayerRegistrationResponse & { webOta?: IWebOtaUpdateEventPayload | null }>

/**
 * Before clearing persisted tenant registration, confirm with Players Directory and (for ambiguous
 * streaks) tenant registration-status. Assigned directory rows are one-shot; after consume, GET
 * `/registration` always returns pairing (`tenantId` null) even while the screen remains in the tenant.
 *
 * `mode: 'revoke'` — a definitive server revoke code already fired. Directory/status probe failures
 * must not block clear (`proceed_clear` on inconclusive); otherwise unregister while pooling can leave
 * the player stuck on tenant UI until the next successful directory round-trip.
 * `mode: 'ambiguous'` / omitted — keep aborting on inconclusive (safer for false-positive streaks).
 */
export async function tryAbortAmbiguousClearViaDirectory(deps: {
  fetchRegistration: TFetchRegistration
  authenticationService: Pick<IAuthenticationService, 'getRegistration' | 'setRegistration'>
  /**
   * When set, abort clear if registration-status still succeeds with stored tenant credentials
   * (directory pairing rows alone are not sufficient — see player-registration-persistence.md).
   */
  confirmStillRegisteredInTenant?: () => Promise<boolean>
  mode?: TRegistrationClearDirectoryGateMode
}): Promise<TAmbiguousDirectoryGateResult> {
  const trustRevokeOnInconclusive = deps.mode === 'revoke'
  const existing = await deps.authenticationService.getRegistration()
  if (existing?.tenantId == null) {
    return 'proceed_clear'
  }
  try {
    const reg = await deps.fetchRegistration({ omitTenantCredentials: true })
    if (reg.tenantId != null) {
      await deps.authenticationService.setRegistration(reg)
      resetPlayerAuthAmbiguousFailureStreak()
      return 'aborted_refreshed'
    }
    if (deps.confirmStillRegisteredInTenant != null) {
      try {
        if (await deps.confirmStillRegisteredInTenant()) {
          resetPlayerAuthAmbiguousFailureStreak()
          return 'aborted_still_registered'
        }
      }
      catch {
        resetPlayerAuthAmbiguousFailureStreak()
        return trustRevokeOnInconclusive ? 'proceed_clear' : 'aborted_inconclusive'
      }
    }
    return 'proceed_clear'
  }
  catch {
    resetPlayerAuthAmbiguousFailureStreak()
    return trustRevokeOnInconclusive ? 'proceed_clear' : 'aborted_inconclusive'
  }
}

/** Delays before each registration-status probe when confirming the screen is still in the tenant. */
export const REGISTRATION_STATUS_CONFIRM_RETRY_DELAYS_MS = [0, 500, 1500] as const

/** True when GET registration-status succeeds with current stored tenant credentials (optional retries). */
export async function confirmPlayerStillRegisteredInTenant(
  fetchRegistrationStatus: () => Promise<unknown | null>,
  retryDelaysMs: readonly number[] = REGISTRATION_STATUS_CONFIRM_RETRY_DELAYS_MS
): Promise<boolean> {
  for (let i = 0; i < retryDelaysMs.length; i++) {
    const delay = retryDelaysMs[i] ?? 0
    if (i > 0 && delay > 0) {
      await new Promise(resolve => setTimeout(resolve, delay))
    }
    try {
      if ((await fetchRegistrationStatus()) != null) {
        return true
      }
    }
    catch {
      // try next delay
    }
  }
  return false
}

export function createRegistrationClearDirectoryGate(deps: {
  fetchRegistration: TFetchRegistration
  authenticationService: Pick<IAuthenticationService, 'getRegistration' | 'setRegistration'>
  fetchRegistrationStatus: () => Promise<unknown | null>
}): (mode: TRegistrationClearDirectoryGateMode) => Promise<TAmbiguousDirectoryGateResult> {
  const confirmStillRegisteredInTenant = () =>
    confirmPlayerStillRegisteredInTenant(deps.fetchRegistrationStatus)
  return mode => tryAbortAmbiguousClearViaDirectory({
    fetchRegistration: deps.fetchRegistration,
    authenticationService: deps.authenticationService,
    confirmStillRegisteredInTenant,
    mode
  })
}
